import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styles from './Login.module.css';
import { authService, proposalComparisonService } from '../../../services/api';
import guestSessionService from '../../../services/guestSessionService';
import TwoFactorLogin from '../TwoFactorLogin/TwoFactorLogin';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: location.state?.email || '',
    password: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(location.state?.message || '');
  const [loading, setLoading] = useState(false);
  
  // Check for expired token parameter in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('expired') === 'true') {
      setError('Your session has expired. Please log in again.');
      // Clear the URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [twoFactorData, setTwoFactorData] = useState(null);

  // Determine the user role based on the current URL path
  const currentPath = window.location.pathname;
  const isHomeOwnerPath = currentPath.startsWith('/homeowner');

  // Clear success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Handle guest session migration after login
  const handleGuestMigration = async (user) => {
    try {
      console.log('=== GUEST MIGRATION START ===');
      
      // Get current guest session ID
      const currentGuestSessionId = localStorage.getItem('guestSessionId') || sessionStorage.getItem('guestSessionId');
      console.log('Current guest session ID:', currentGuestSessionId);
      
      // Get all guest sessions from local storage
      const guestSessions = guestSessionService.getGuestSessions();
      console.log('Found guest sessions:', guestSessions.length);
      
      // Scenario 1: User is viewing a comparison and logs in
      if (location.state?.returnTo && location.state.returnTo.includes('/comparisons/')) {
        console.log('Scenario 1: Auto-migrating current comparison session');
        if (currentGuestSessionId) {
          console.log('Auto-migrating current comparison session...');
          await proposalComparisonService.migrateGuestToUser(currentGuestSessionId, user.id);
          
          // Find and remove the corresponding guest session from local storage
          const correspondingSession = guestSessions.find(session => 
            session.data?.guest_session_id === currentGuestSessionId ||
            session.comparisonId === location.state.returnTo.split('/comparisons/')[1].split('?')[0]
          );
          if (correspondingSession) {
            guestSessionService.deleteGuestSession(correspondingSession.id);
            console.log('Removed corresponding guest session:', correspondingSession.id);
          }
          
          // Navigate to the comparison as authenticated user
          const comparisonId = location.state.returnTo.split('/comparisons/')[1].split('?')[0];
          console.log('Navigating to comparison:', comparisonId);
          navigate(`/homeowner/comparisons/${comparisonId}`);
        }
      } 
      // Scenario 2: User has guest sessions and logs in from home page
      else if (guestSessions.length > 0) {
        console.log('Scenario 2: Setting up import popup for', guestSessions.length, 'sessions');
        // Store migration data for the import popup
        localStorage.setItem('pendingGuestMigration', JSON.stringify({
          guestSessions,
          userId: user.id,
          showImportPopup: true
        }));
        console.log('Stored pending migration data');
        
        // Don't clear guest session data here - let the migration popup handle it
        // This allows users to continue as guests if they choose not to migrate
      }
      
      // CRITICAL: Always clear guest session data after login to prevent conflicts
      // This ensures there's no confusion between guest and authenticated sessions
      console.log('Clearing guest session data to prevent auth conflicts...');
      localStorage.removeItem('guestSessionId');
      sessionStorage.removeItem('guestSessionId');
      localStorage.removeItem('guestSessionCreated');
      
      console.log('=== GUEST MIGRATION END ===');
    } catch (error) {
      console.error('Error during guest migration:', error);
      // Even if migration fails, clear guest session data to prevent auth conflicts
      localStorage.removeItem('guestSessionId');
      sessionStorage.removeItem('guestSessionId');
      localStorage.removeItem('guestSessionCreated');
    }
  };

  const handleLoginSuccess = async () => {
    const { state } = location;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    // Handle guest session migration
    await handleGuestMigration(user);
    
    if (state?.redirectUrl) {
      navigate(state.redirectUrl);
    } else if (state?.returnTo) {
      navigate(state.returnTo);
    } else {
      // Check for saved invitation URL (fallback)
      const inviteRedirectUrl = localStorage.getItem('inviteRedirectUrl');
      if (inviteRedirectUrl) {
        localStorage.removeItem('inviteRedirectUrl');
        navigate(inviteRedirectUrl);
      } else {
        // Navigate based on user role only
        if (user.role === 'admin') {
          navigate('/admin/dashboard');
        } else if (user.role === 'homeowner') {
          // Homeowner users always go to the dashboard where migration popup will show
          navigate('/homeowner/dashboard');
        } else {
          // All other users (contractor/user role) go to the contractor dashboard
          navigate('/homeowner/dashboard');
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authService.login(formData);

      if (response.data.status === 'success') {
        // Normal login without 2FA
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        // Set flag to indicate this is a fresh login
        sessionStorage.setItem('freshLogin', 'true');
        
        // Trigger storage event
        window.dispatchEvent(new Event('storage'));

        await handleLoginSuccess();
      } else if (response.data.status === 'requires_2fa') {
        // 2FA is required
        setTwoFactorData({
          tempSessionId: response.data.tempSessionId,
          twoFactorMethod: response.data.twoFactorMethod,
          user: response.data.user
        });
        setShowTwoFactor(true);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactorComplete = async (user, token) => {
    // Store the token and user data
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    
    // Set flag to indicate this is a fresh login
    sessionStorage.setItem('freshLogin', 'true');
    
    // Trigger storage event
    window.dispatchEvent(new Event('storage'));

    await handleLoginSuccess();
  };

  const handleBackToLogin = () => {
    setShowTwoFactor(false);
    setTwoFactorData(null);
    setError('');
  };

  // Update login text based on user role
  const getRoleSpecificText = () => {
    if (isHomeOwnerPath) {
      return {
        title: 'Welcome Back, Home Owner',
        subtitle: 'Login to your homeowner account'
      };
    }
    return {
      title: 'Welcome Back',
      subtitle: 'Login to your account'
    };
  };

  const roleText = getRoleSpecificText();

  // Show 2FA screen if required
  if (showTwoFactor && twoFactorData) {
    return (
      <TwoFactorLogin
        tempSessionId={twoFactorData.tempSessionId}
        twoFactorMethod={twoFactorData.twoFactorMethod}
        user={twoFactorData.user}
        onLoginComplete={handleTwoFactorComplete}
        onBackToLogin={handleBackToLogin}
      />
    );
  }

  return (
    <div className={styles.authContainer}>
      <div className={styles.authBackground}>
        <div className={styles.shape}></div>
        <div className={styles.shape}></div>
        <div className={styles.shape}></div>
      </div>
      
      <div className={styles.authCard}>
        <div className={styles.authHeader}>
          <h2 className={styles.title}>{roleText.title}</h2>
          <p className={styles.authSubtitle}>{roleText.subtitle}</p>
        </div>
        
        {success && (
          <div className={styles.success}>
            <i className="fas fa-check-circle"></i>
            {success}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className={styles.authForm}>
          <div className={styles.formGroup}>
            <div className={styles.inputWrapper}>
              <i className="fas fa-envelope"></i>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <div className={styles.inputWrapper}>
              <i className="fas fa-lock"></i>
              <input
                type="password"
                id="password"
                name="password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {error && (
            <div className={styles.error}>
              <i className="fas fa-exclamation-circle"></i>
              {error}
            </div>
          )}

          <button 
            type="submit" 
            className={`${styles.authButton} ${loading ? styles.loading : ''}`}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className={styles.spinner}></div>
                <span>Logging in...</span>
              </>
            ) : (
              <>
                <span>Login</span>
                <i className="fas fa-arrow-right"></i>
              </>
            )}
          </button>
        </form>
        
        <div className={styles.authLinks}>
          <Link to={isHomeOwnerPath ? "/homeowner/forgot-password" : "/forgot-password"} className={styles.forgotPassword}>
            <i className="fas fa-key"></i>
            Forgot Password?
          </Link>
          <div className={styles.divider}>
            <span>or</span>
          </div>
          <p className={styles.authRedirect}>
            Don't have an account?{' '}
            <Link to={isHomeOwnerPath ? "/homeowner/signup" : "/signup"}>Sign up now</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login; 